'use client';

import { createContext, useCallback, useEffect, useState } from 'react';
import dynamic from 'next/dynamic';

// The panel is a demo/impersonation surface reachable only via Cmd+Shift+D.
// Loading it lazily keeps it out of every page's initial bundle without
// taking the capability away from a live demo.
const DoppelgangerPanel = dynamic(
  () => import('./doppelganger-panel').then((m) => m.DoppelgangerPanel),
  { ssr: false },
);

interface DoppelgangerContextValue {
  isOpen: boolean;
  toggle: () => void;
  close: () => void;
}

const DoppelgangerContext = createContext<DoppelgangerContextValue>({
  isOpen: false,
  toggle: () => {},
  close: () => {},
});

export function DoppelgangerProvider({
  children,
}: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const toggle = useCallback(() => {
    setIsOpen((v) => {
      if (!v) setHasOpened(true);
      return !v;
    });
  }, []);
  const close = useCallback(() => setIsOpen(false), []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (
        (e.metaKey || e.ctrlKey) &&
        e.shiftKey &&
        e.key.toLowerCase() === 'd'
      ) {
        e.preventDefault();
        toggle();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [toggle]);

  useEffect(() => {
    if (isOpen) {
      document.documentElement.setAttribute('data-demo-panel', 'open');
    } else {
      document.documentElement.removeAttribute('data-demo-panel');
    }
  }, [isOpen]);

  return (
    <DoppelgangerContext.Provider value={{ isOpen, toggle, close }}>
      {children}
      {/* Mounted on first open and kept thereafter, so the slide-out
          transition still has something to animate. */}
      {hasOpened ? <DoppelgangerPanel isOpen={isOpen} onClose={close} /> : null}
    </DoppelgangerContext.Provider>
  );
}
